// pages/orders/ReceiptModal.jsx
// Prints the receipt document for an order. `receipt` is OPTIONAL: pass it from the
// Receipts page to add a "Payment Received" section + balance for that specific
// receipt row. Without it (Orders list / Order detail) the document renders as before.
import { useRef } from "react";
import { Modal }  from "../../components/ui";
import { fmt }    from "../../utils/helpers";
import { useAuth } from "../../contexts/AuthContext";
import { getBranding } from "../../utils/branding";

export default function ReceiptModal({ order, receipt, onClose }) {
  const contentRef = useRef(null);
  const b = getBranding(useAuth().company);

  const balance = Math.max(0, (order.amount || 0) - (receipt?.amount || 0));

  const handlePrint = () => window.print();

  // Opens a new window with receipt HTML and triggers print-to-PDF
  const handleDownloadPDF = () => {
    const html = contentRef.current?.innerHTML ?? "";
    const win  = window.open("", "_blank");
    if (!win) return;
    win.document.write(`
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="UTF-8" />
          <title>Receipt – ${order.id}</title>
          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@400;500;600&display=swap" rel="stylesheet" />
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body  { font-family: 'DM Sans', sans-serif; background: #fff; color: #111; padding: 32px; }
            h1,h2 { font-family: 'Playfair Display', serif; }
            .receipt-wrap        { max-width: 520px; margin: 0 auto; }
            .receipt-header      { text-align: center; border-bottom: 2px solid #111; padding-bottom: 18px; margin-bottom: 20px; }
            .receipt-logo {
              width: 80px;
              height: auto;
              object-fit: contain;
              display: block;
              margin: 0 auto 10px;
            }
            .receipt-title       { font-family: 'Playfair Display', serif; font-size: 26px; }
            .receipt-subtitle    { font-size: 12px; color: #666; margin-top: 3px; }
            .receipt-section     { margin: 16px 0; }
            .receipt-section-title { font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 10px; color: #333; padding-bottom: 4px; border-bottom: 1px solid #eee; }
            .receipt-row         { display: flex; justify-content: space-between; align-items: center; padding: 6px 0; border-bottom: 1px dotted #e0e0e0; font-size: 12.5px; gap: 16px; }
            .receipt-row:last-child { border-bottom: none; }
            .receipt-row-label   { color: #555; }
            .receipt-row-value   { font-weight: 500; text-align: right; }
            .receipt-total       { display: flex; justify-content: space-between; padding: 13px 0 0; border-top: 2px solid #111; font-weight: 700; font-size: 16px; margin-top: 6px; }
            .receipt-footer      { text-align: center; margin-top: 22px; font-size: 11.5px; color: #888; border-top: 1px solid #eee; padding-top: 14px; line-height: 1.6; }
          </style>
        </head>
        <body>${html}</body>
      </html>
    `);
    win.document.close();
    // setTimeout(() => win.print(), 400);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Order Receipt"
      subtitle={receipt?.id ? `Receipt ${receipt.id} · Order ${order.id}` : order.id}
      large
      footer={
        <div style={{ display: "flex", gap: 8, width: "100%", justifyContent: "space-between" }}>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
          <div style={{ display: "flex", gap: 8 }}>
            {/* <button className="btn btn-secondary" onClick={handlePrint}>🖨️ Print</button> */}
            <button className="btn btn-primary"   onClick={handleDownloadPDF}>⬇ Download PDF</button>
          </div>
        </div>
      }
    >
      {/* Receipt content — also rendered into the PDF window */}
      <div ref={contentRef} className="receipt-wrap">
        {/* Header */}
        <div className="receipt-header">
          <img src={b.logoSrc} alt="" className="receipt-logo"/>
          <div className="receipt-title">{b.shopName}</div>
          <div className="receipt-subtitle">Order Receipt</div>
        </div>

        {/* Order info */}
        <div className="receipt-section">
          <div className="receipt-section-title">Order Information</div>
          <ReceiptRow label="Order ID"      value={<strong>{order.id}</strong>} />
          <ReceiptRow label="Customer"      value={order.customerName} />
          <ReceiptRow label="Order Date"    value={fmt(order.orderDate)} />
          <ReceiptRow label="Delivery Date" value={fmt(order.deliveryDate)} />
          <ReceiptRow
            label="Status"
            value={<span style={{ color: "#4caf7a", fontWeight: 700 }}>✓ {order.status}</span>}
          />
          {order.assignedKarigarName && (
            <ReceiptRow label="Karigar" value={order.assignedKarigarName} />
          )}
        </div>

        {/* Stitch details */}
        <div className="receipt-section">
          <div className="receipt-section-title">Stitch Details</div>
          {order.stitchDetails?.map((s, i) => (
            <ReceiptRow
              key={i}
              label={`${s.type}${s.notes ? ` (${s.notes})` : ""}`}
              value={`×${s.qty}`}
            />
          ))}
        </div>

        {/* Material details */}
        <div className="receipt-section">
          <div className="receipt-section-title">Material Details</div>
          {order.materialDetails?.map((m, i) => (
            <ReceiptRow
              key={i}
              label={`${m.name}${m.notes ? ` — ${m.notes}` : ""}`}
              value={`${m.quantity} ${m.unit}`}
            />
          ))}
        </div>

        {/* Total */}
        {order.amount > 0 && (
          <div className="receipt-total">
            <span>Total Amount</span>
            <span>₹{order.amount?.toLocaleString()}</span>
          </div>
        )}

        {/* Payment received — only when printed from a specific receipt row */}
        {receipt && (
          <div className="receipt-section">
            <div className="receipt-section-title">Payment Received</div>
            {receipt.id && <ReceiptRow label="Receipt No." value={<strong>{receipt.id}</strong>} />}
            <ReceiptRow label="Amount Received" value={`₹${(receipt.amount || 0).toLocaleString()}`} />
            <ReceiptRow label="Mode" value={receipt.mode || "—"} />
            <ReceiptRow label="Received On" value={fmt(receipt.date)} />
            {receipt.remarks && <ReceiptRow label="Remarks" value={receipt.remarks} />}
            {order.amount > 0 && (
              <div className="receipt-total">
                <span>Balance Due</span>
                <span>₹{balance.toLocaleString()}</span>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="receipt-footer">
          Thank you for choosing <strong>{b.shopName}</strong>!<br />
          Please retain this receipt for your records.<br />
          <span style={{ fontSize: 10, color: "#aaa" }}>
            Generated on{" "}
            {new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
          </span>
        </div>
      </div>
    </Modal>
  );
}

// ── Small helper ──────────────────────────────────────────────
function ReceiptRow({ label, value }) {
  return (
    <div className="receipt-row">
      <span className="receipt-row-label">{label}</span>
      <span className="receipt-row-value">{value}</span>
    </div>
  );
}
